import { colors } from '@packages/core/styles';
import { type FC, type MouseEvent, type ReactNode } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';

const TOOLTIP_DELAY = { hide: 400, show: 250 } as const;

const useStyles = createUseStyles({
    customTooltip: {
        fontSize: 16,
        marginTop: '-4px !important',
        padding: '8px 12px',
        textAlign: 'center',
    },
    iconButton: {
        alignItems: 'center',
        background: 'none',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        display: 'flex',
        height: 40,
        justifyContent: 'center',
        padding: 0,
        width: 40,
        '&:hover': {
            backgroundColor: colors.blueFour,
        },
        '&:disabled': {
            cursor: 'not-allowed',
            opacity: 0.5,
        },
        '&:disabled:hover': {
            backgroundColor: 'transparent',
        },
        '&:focus-visible': {
            outline: `2px solid ${colors.blueOne}`,
            outlineOffset: 2,
        },
    },
});

type Props = {
    children: ReactNode;
    disabled?: boolean;
    id: string;
    label: string;
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
};

export const ActionIconButton: FC<Props> = ({ children, disabled = false, id, label, onClick }) => {
    const classes = useStyles();

    const button = (
        <button aria-label={label} className={classes.iconButton} disabled={disabled} onClick={onClick} type="button">
            {children}
        </button>
    );

    if (disabled) {
        return button;
    }

    return (
        <OverlayTrigger
            delay={TOOLTIP_DELAY}
            overlay={
                <Tooltip className={classes.customTooltip} id={id}>
                    {label}
                </Tooltip>
            }
            placement="bottom"
        >
            {button}
        </OverlayTrigger>
    );
};
